/**
 * Phase 6 Plan 02 — InspectionRow (D-134 collapsed + D-135 expanded).
 *
 * Collapsed: clickable button (aria-expanded). Renders <bdi>{date}</bdi> ·
 * <bdi>{worker_name}</bdi> · {ticks_summary} · <PhotoCountBadge> [optional
 * pencil + HIST_PM_EDIT_COUNT]. Past ~40 chars CSS text-overflow:ellipsis.
 *
 * Expanded: <ExpandedRow> renders Tab1Section + Tab2Section + PhotoStrip +
 * MetadataBlock (GPS + saved_at — synced_at row INTENTIONALLY ABSENT per
 * D-148) + AuditTrailTable (only when pm_edit_count > 0) + EditButton
 * (reuses Plan 04-04 InspectionEditModal via setEditingInspection callback).
 *
 * data-testid="history-row-{id}" for Playwright addressing (NFR-PERF-4).
 *
 * @SpecRef("5.1")
 * @SpecRef("5.2")
 * @SpecRef("5.4")
 */
import { useMemo } from "react";
import { FormattedMessage, FormattedDate, FormattedTime, useIntl } from "react-intl";
import { Pencil, ChevronDown, ChevronRight } from "lucide-react";
import type { InspectionRead } from "../../../api/inspections";
import { Button } from "../../../components/Button";
import { PhotoCountBadge } from "./PhotoCountBadge";
import { PhotoStrip } from "./PhotoStrip";
import { AuditTrailTable } from "./AuditTrailTable";

interface InspectionRowProps {
  inspection: InspectionRead;
  expanded: boolean;
  onToggleExpand: () => void;
   
  onEdit: (insp: InspectionRead) => void;
}

const TAB1_TICK_LABELS: { field: keyof InspectionRead; labelId: string }[] = [
  { field: "tab1_cleaned", labelId: "HIST_TICK_CLEANED" },
  { field: "tab1_pipe_blocked", labelId: "HIST_TICK_PIPE_BLOCKED" },
  { field: "tab1_structure_damaged", labelId: "HIST_TICK_STRUCTURE_DAMAGED" },
  { field: "tab1_cover_needs_level_adjustment", labelId: "HIST_TICK_COVER_LEVEL" },
];

const TAB2_TICK_LABELS: { field: keyof InspectionRead; labelId: string }[] = [
  { field: "tab2_repaired_internal_structure", labelId: "HIST_TICK_REPAIRED_STRUCTURE" },
  { field: "tab2_adjusted_cover_level", labelId: "HIST_TICK_ADJUSTED_COVER" },
];

function buildTicksSummary(
  insp: InspectionRead,
  intl: ReturnType<typeof useIntl>,
): string {
  const parts: string[] = [];
  for (const t of TAB1_TICK_LABELS) {
    if (insp[t.field] === true) parts.push(intl.formatMessage({ id: t.labelId }));
  }
  for (const t of TAB2_TICK_LABELS) {
    if (insp[t.field] === true) parts.push(intl.formatMessage({ id: t.labelId }));
  }
  if (insp.tab2_applied_coating && insp.tab2_applied_coating_type) {
    const typeLabel = intl.formatMessage({
      id:
        insp.tab2_applied_coating_type === "bitumen"
          ? "LABEL_COATING_BITUMEN"
          : "LABEL_COATING_EPOXY",
    });
    parts.push(
      intl.formatMessage({ id: "HIST_TICK_APPLIED_COATING" }, { type: typeLabel }),
    );
  } else if (insp.tab2_applied_coating) {
    parts.push(intl.formatMessage({ id: "HIST_TICK_APPLIED_COATING" }, { type: "" }));
  }
  return parts.join(" · ");
}

export function InspectionRow({
  inspection,
  expanded,
  onToggleExpand,
  onEdit,
}: InspectionRowProps) {
  const intl = useIntl();
  const ticksSummary = useMemo(
    () => buildTicksSummary(inspection, intl),
    [inspection, intl],
  );
  return (
    <li
      data-testid={`history-row-${inspection.id}`}
      className="border border-divider rounded-md bg-surface-raised"
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggleExpand}
        data-testid={`history-row-toggle-${inspection.id}`}
        className="w-full flex items-center gap-xs ps-sm pe-sm pbs-xs pbe-xs text-start hover:bg-surface"
      >
        {expanded ? (
          <ChevronDown size={14} aria-hidden="true" className="shrink-0 text-text-secondary" />
        ) : (
          <ChevronRight size={14} aria-hidden="true" className="shrink-0 text-text-secondary" />
        )}
        <span className="flex-1 flex flex-col gap-xxs min-w-0">
          <span className="flex items-center gap-xs text-xs text-on-surface-variant flex-wrap">
            <bdi>
              <FormattedDate
                value={inspection.saved_at}
                year="numeric"
                month="2-digit"
                day="2-digit"
              />
              {" "}
              <FormattedTime
                value={inspection.saved_at}
                hour="2-digit"
                minute="2-digit"
                hour12={false}
              />
            </bdi>
            <span aria-hidden="true">·</span>
            <bdi className="font-medium text-on-surface">
              {inspection.worker_name ?? `#${inspection.worker_id}`}
            </bdi>
            <span aria-hidden="true">·</span>
            <span className="text-text-secondary truncate" title={ticksSummary}>
              {ticksSummary || "—"}
            </span>
            <span aria-hidden="true">·</span>
            <PhotoCountBadge
              total={inspection.photo_count}
              uploading={Math.max(
                0,
                inspection.photo_count - inspection.photo_uploaded_count,
              )}
              data-testid={`history-row-photo-badge-${inspection.id}`}
            />
            {inspection.pm_edit_count > 0 && (
              <span
                data-testid={`history-row-pm-edit-${inspection.id}`}
                className="inline-flex items-center gap-xxs text-xs text-warning"
              >
                <Pencil
                  size={12}
                  aria-label={intl.formatMessage({ id: "LABEL_PENCIL_ICON_DESC" })}
                />
                <FormattedMessage
                  id="HIST_PM_EDIT_COUNT"
                  values={{ count: inspection.pm_edit_count }}
                />
              </span>
            )}
          </span>
        </span>
      </button>
      {expanded && <ExpandedRow inspection={inspection} onEdit={onEdit} />}
    </li>
  );
}

interface ExpandedRowProps {
  inspection: InspectionRead;
   
  onEdit: (insp: InspectionRead) => void;
}

function BoolRow({ labelId, value }: { labelId: string; value: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs pbs-xs pbe-xs border-bs border-divider">
      <span className="text-on-surface-variant">
        <FormattedMessage id={labelId} />
      </span>
      <span
        className={`font-semibold ${value ? "text-on-secondary-container" : "text-text-secondary"}`}
      >
        {value ? "✓" : "—"}
      </span>
    </div>
  );
}

function ExpandedRow({ inspection, onEdit }: ExpandedRowProps) {
  const showAudit = inspection.pm_edit_count > 0;
  return (
    <div
      data-density="compact"
      className="ps-sm pe-sm pbs-xs pbe-md border-bs border-divider bg-surface"
      data-testid={`history-row-expanded-${inspection.id}`}
    >
      <h4 className="text-sm font-semibold text-on-surface mbe-xs">
        <FormattedMessage id="HIST_DETAILS_HEADING" />
      </h4>
      {/* Tab 1 */}
      <fieldset className="mbe-xs">
        <legend className="text-xs font-semibold text-on-surface-variant mbe-xxs">
          <FormattedMessage id="HIST_FILTER_TAB1_HEADER" />
        </legend>
        <div className="flex flex-col">
          <BoolRow labelId="HIST_TICK_CLEANED" value={inspection.tab1_cleaned} />
          <BoolRow
            labelId="HIST_TICK_PIPE_BLOCKED"
            value={inspection.tab1_pipe_blocked}
          />
          <BoolRow
            labelId="HIST_TICK_STRUCTURE_DAMAGED"
            value={inspection.tab1_structure_damaged}
          />
          <BoolRow
            labelId="HIST_TICK_COVER_LEVEL"
            value={inspection.tab1_cover_needs_level_adjustment}
          />
          <div className="flex items-center justify-between text-xs pbs-xxs pbe-xxs border-bs border-divider">
            <span className="text-on-surface-variant">
              <FormattedMessage id="LABEL_COATING_TITLE" />
            </span>
            <span className="font-semibold text-on-surface-variant">
              {/* R3-003/D3 — 'none' (explicit observation) and null (no
                  signal) both read as None; the fall-through must never
                  swallow 'none' as Epoxy. */}
              {inspection.tab1_coating === "bitumen" ? (
                <FormattedMessage id="LABEL_COATING_BITUMEN" />
              ) : inspection.tab1_coating === "epoxy" ? (
                <FormattedMessage id="LABEL_COATING_EPOXY" />
              ) : (
                <FormattedMessage id="LABEL_COATING_NONE" />
              )}
            </span>
          </div>
        </div>
      </fieldset>
      {/* Tab 2 */}
      <fieldset className="mbe-xs">
        <legend className="text-xs font-semibold text-on-surface-variant mbe-xxs">
          <FormattedMessage id="HIST_FILTER_TAB2_HEADER" />
        </legend>
        <div className="flex flex-col">
          <BoolRow
            labelId="HIST_TICK_REPAIRED_STRUCTURE"
            value={inspection.tab2_repaired_internal_structure}
          />
          <BoolRow
            labelId="HIST_TICK_ADJUSTED_COVER"
            value={inspection.tab2_adjusted_cover_level}
          />
          <BoolRow
            labelId="LABEL_CHECKBOX_APPLIED_COATING"
            value={inspection.tab2_applied_coating}
          />
        </div>
      </fieldset>
      {/* Photos */}
      <PhotoStrip inspectionId={inspection.id} expanded={true} />
      {/* Metadata */}
      <section
        className="mbs-sm flex flex-col gap-xxs text-xs"
        data-testid={`history-row-metadata-${inspection.id}`}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold text-on-surface-variant">
            <FormattedMessage id="HIST_GPS_LABEL" />
          </span>
          <span className="text-on-surface-variant">
            <bdi>
              {inspection.gps_lat !== null && inspection.gps_lng !== null
                ? `${inspection.gps_lat.toFixed(6)}, ${inspection.gps_lng.toFixed(6)}`
                : "—"}
            </bdi>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-on-surface-variant">
            <FormattedMessage id="HIST_SAVED_AT_LABEL" />
          </span>
          <span className="text-on-surface-variant">
            <bdi>
              <FormattedDate
                value={inspection.saved_at}
                year="numeric"
                month="2-digit"
                day="2-digit"
              />
              {" "}
              <FormattedTime
                value={inspection.saved_at}
                hour="2-digit"
                minute="2-digit"
                hour12={false}
              />
            </bdi>
          </span>
        </div>
        {/* synced_at row INTENTIONALLY ABSENT per D-148 (no physical column server-side). */}
      </section>
      {/* Audit trail */}
      {showAudit && <AuditTrailTable inspectionId={inspection.id} />}
      {/* Edit button — reuses Plan 04-04 InspectionEditModal */}
      <div className="mbs-md flex justify-end">
        <Button
          variant="secondary"
          onClick={() => onEdit(inspection)}
          data-testid={`history-row-edit-${inspection.id}`}
        >
          <span className="inline-flex items-center gap-xs">
            <Pencil size={14} aria-hidden="true" />
            <FormattedMessage id="LABEL_EDIT" />
          </span>
        </Button>
      </div>
    </div>
  );
}
